import React, { useState, useEffect } from 'react';

interface KnowledgeGraphPreviewProps {
  onClick?: () => void;
}

interface GraphNode {
  id: string;
  x: number;
  y: number;
  label: string;
  mastery: number;
  subject: 'maths' | 'english' | 'science';
  connections: string[];
}

const KnowledgeGraphPreview: React.FC<KnowledgeGraphPreviewProps> = ({ onClick }) => {
  const [activeNode, setActiveNode] = useState<string | null>(null);
  const [pulsingNode, setPulsingNode] = useState<string | null>(null);
  const [decayingNode, setDecayingNode] = useState<string | null>(null);

  const nodes: GraphNode[] = [
    { id: 'n1', x: 160, y: 40, label: 'Numbers', mastery: 95, subject: 'maths', connections: ['n2', 'n3'] },
    { id: 'n2', x: 80, y: 100, label: 'Addition', mastery: 88, subject: 'maths', connections: ['n4'] },
    { id: 'n3', x: 240, y: 100, label: 'Subtraction', mastery: 82, subject: 'maths', connections: ['n4'] },
    { id: 'n4', x: 160, y: 160, label: 'Multiplication', mastery: 65, subject: 'maths', connections: ['n5', 'n6'] },
    { id: 'n5', x: 80, y: 220, label: 'Fractions', mastery: 45, subject: 'maths', connections: [] },
    { id: 'n6', x: 240, y: 220, label: 'Division', mastery: 38, subject: 'maths', connections: [] },
  ];

  // Simulate mastery updates
  useEffect(() => {
    const interval = setInterval(() => {
      const randomNode = nodes[Math.floor(Math.random() * nodes.length)];
      setPulsingNode(randomNode.id);
      setTimeout(() => setPulsingNode(null), 800);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Simulate decay warning
  useEffect(() => {
    const interval = setInterval(() => {
      setDecayingNode('n5');
      setTimeout(() => setDecayingNode(null), 3000);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  // Cycle active node for highlight
  useEffect(() => {
    const interval = setInterval(() => {
      const idx = nodes.findIndex(n => n.id === activeNode);
      const nextIdx = (idx + 1) % nodes.length;
      setActiveNode(nodes[nextIdx].id);
    }, 2500);
    setActiveNode('n4');
    return () => clearInterval(interval);
  }, []);

  const getMasteryColor = (mastery: number) => {
    if (mastery >= 80) return '#10b981';
    if (mastery >= 60) return '#3b82f6';
    if (mastery >= 40) return '#f59e0b';
    return '#ef4444';
  };

  return (
    <div
      className="relative cursor-pointer transition-all duration-300 hover:scale-[1.02] group"
      onClick={onClick}
      style={{
        background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        overflow: 'hidden',
        width: '320px',
      }}
    >
      {/* Header */}
      <div
        className="px-4 py-2 border-b flex items-center justify-between"
        style={{
          borderColor: 'rgba(255, 255, 255, 0.1)',
          background: 'rgba(59, 130, 246, 0.05)',
        }}
      >
        <span className="text-xs font-semibold" style={{ color: '#94a3b8' }}>
          Knowledge Graph
        </span>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#10b981' }} />
          <span className="text-[10px]" style={{ color: '#10b981' }}>
            LIVE
          </span>
        </div>
      </div>

      {/* Graph Visualization */}
      <div className="p-2">
        <svg width="300" height="260" className="block mx-auto">
          <defs>
            <filter id="kg-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="3" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <linearGradient id="connection-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#10b981" stopOpacity="0.6" />
            </linearGradient>
          </defs>

          {/* Connections */}
          {nodes.map(node =>
            node.connections.map(targetId => {
              const target = nodes.find(n => n.id === targetId);
              if (!target) return null;
              const isActive = activeNode === node.id || activeNode === targetId;
              return (
                <line
                  key={`${node.id}-${targetId}`}
                  x1={node.x}
                  y1={node.y}
                  x2={target.x}
                  y2={target.y}
                  stroke={isActive ? '#3b82f6' : '#334155'}
                  strokeWidth={isActive ? 2 : 1.5}
                  opacity={isActive ? 0.8 : 0.4}
                  style={{ transition: 'all 0.3s ease' }}
                />
              );
            })
          )}

          {/* Nodes */}
          {nodes.map(node => {
            const isActive = activeNode === node.id;
            const isPulsing = pulsingNode === node.id;
            const isDecaying = decayingNode === node.id;
            const masteryColor = getMasteryColor(node.mastery);

            return (
              <g key={node.id}>
                {/* Decay warning ring */}
                {isDecaying && (
                  <circle
                    cx={node.x}
                    cy={node.y}
                    r={28}
                    fill="none"
                    stroke="#f59e0b"
                    strokeWidth="2"
                    strokeDasharray="4 4"
                    opacity="0.8"
                    className="animate-spin"
                    style={{ transformOrigin: `${node.x}px ${node.y}px`, animationDuration: '3s' }}
                  />
                )}

                {/* Pulse ring */}
                {isPulsing && (
                  <circle
                    cx={node.x}
                    cy={node.y}
                    r={24}
                    fill="none"
                    stroke={masteryColor}
                    strokeWidth="2"
                    opacity="0.6"
                    className="animate-ping"
                  />
                )}

                {/* Background circle */}
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={20}
                  fill="#1e293b"
                  stroke={isActive ? masteryColor : '#334155'}
                  strokeWidth={isActive ? 3 : 2}
                  filter={isActive ? 'url(#kg-glow)' : undefined}
                  style={{ transition: 'all 0.3s ease' }}
                />

                {/* Mastery arc */}
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={17}
                  fill="none"
                  stroke={masteryColor}
                  strokeWidth="3"
                  strokeDasharray={`${(node.mastery / 100) * 107} 107`}
                  strokeLinecap="round"
                  transform={`rotate(-90 ${node.x} ${node.y})`}
                  opacity={0.9}
                />

                {/* Center percentage */}
                <text
                  x={node.x}
                  y={node.y + 1}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="white"
                  fontSize="9"
                  fontWeight="bold"
                >
                  {node.mastery}%
                </text>

                {/* Label */}
                <text
                  x={node.x}
                  y={node.y + 32}
                  textAnchor="middle"
                  fill={isActive ? '#e2e8f0' : '#64748b'}
                  fontSize="9"
                  fontWeight="500"
                >
                  {node.label}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Legend */}
      <div className="px-3 pb-2">
        <div className="flex items-center justify-between text-[9px]" style={{ color: '#64748b' }}>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full" style={{ background: '#10b981' }} />
              <span>Mastered</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full" style={{ background: '#f59e0b' }} />
              <span>Learning</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full" style={{ background: '#ef4444' }} />
              <span>Needs Work</span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div
        className="px-4 py-2 flex items-center justify-between"
        style={{
          background: 'rgba(255, 255, 255, 0.02)',
          borderTop: '1px solid rgba(255, 255, 255, 0.05)',
        }}
      >
        <div className="flex items-center gap-2">
          <span className="text-[10px]" style={{ color: '#64748b' }}>
            6 concepts tracked
          </span>
        </div>
        {decayingNode && (
          <div className="flex items-center gap-1">
            <span className="text-[10px]" style={{ color: '#f59e0b' }}>
              ⚠ Review needed
            </span>
          </div>
        )}
      </div>

      {/* Expand overlay */}
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/20">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.9) 0%, rgba(6, 182, 212, 0.9) 100%)',
            boxShadow: '0 8px 32px rgba(59, 130, 246, 0.4)',
          }}
        >
          <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeGraphPreview;
